import React, { useRef, useEffect, useState } from "react";
import { Box, Typography, useTheme } from "@mui/material";
import {
  ChartContainer,
  BarPlot,
  LinePlot,
  ChartsXAxis,
  ChartsYAxis,
  ChartsGrid,
  ChartsLegend,
  ChartsTooltip,
} from "@mui/x-charts";
import { formatChartTitle } from "../../utils";

interface CombinedBarLineChartData {
  [key: string]: string | number | null | undefined;
}

interface BarSeries {
  dataKey: string;
  label: string;
  color?: string;
}

interface LineSeries {
  dataKey: string;
  label: string;
  color?: string;
}

interface CombinedBarLineChartProps {
  data: CombinedBarLineChartData[];
  xAxisKey: string;
  barSeries: BarSeries[];
  lineSeries: LineSeries[];
  title?: string;
  height?: number;
  barYAxisMin?: number;
  barYAxisMax?: number;
  lineYAxisMin?: number;
  lineYAxisMax?: number;
}

export const CombinedBarLineChart: React.FC<CombinedBarLineChartProps> = ({
  data,
  xAxisKey,
  barSeries,
  lineSeries,
  title,
  height = 300,
  barYAxisMin,
  barYAxisMax,
  lineYAxisMin,
  lineYAxisMax,
}) => {
  const theme = useTheme();
  const containerRef = useRef<HTMLDivElement>(null);
  const formattedTitle = formatChartTitle(title);

  const barChartSeries = barSeries.map((s, index) => ({
    type: "bar" as const,
    dataKey: s.dataKey,
    label: s.label,
    color: s.color || theme.palette.primary.main,
    yAxisId: "bar-axis",
    id: `bar-${index}`,
  }));

  const lineChartSeries = lineSeries.map((s, index) => ({
    type: "line" as const,
    dataKey: s.dataKey,
    label: s.label,
    color: s.color || theme.palette.success.main,
    yAxisId: "line-axis",
    id: `line-${index}`,
  }));

  const allSeries = [...barChartSeries, ...lineChartSeries];

  return (
    <Box ref={containerRef} sx={{ width: "100%" }}>
      {formattedTitle && (
        <Typography variant="h6" fontWeight={600} gutterBottom>
          {formattedTitle}
        </Typography>
      )}
      <ChartContainer
        dataset={data}
        height={height}
        series={allSeries}
        xAxis={[
          {
            scaleType: "band",
            dataKey: xAxisKey,
            disableTicks: true,
          },
        ]}
        yAxis={[
          {
            id: "bar-axis",
            min: barYAxisMin,
            max: barYAxisMax,
            disableTicks: true,
          },
          {
            id: "line-axis",
            min: lineYAxisMin,
            max: lineYAxisMax,
            disableTicks: true,
            position: "right",
          },
        ]}
        margin={{ left: 0, right: 0, top: 10, bottom: 80 }}
      >
        <ChartsGrid vertical horizontal />
        <BarPlot borderRadius={12} />
        <LinePlot />
        <ChartsXAxis />
        <ChartsYAxis axisId="bar-axis" />
        <ChartsYAxis axisId="line-axis" />
        <ChartsLegend />
        <ChartsTooltip />
      </ChartContainer>
    </Box>
  );
};
